import type { Injector, Signal, WritableSignal } from '@angular/core';
import { computed, DestroyRef, inject, runInInjectionContext, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { StorageService } from '@kerberos-compliance/compliance-shared-ui';
import type { Observable } from 'rxjs';
import { debounceTime } from 'rxjs';

type SignalMap = Record<string, Signal<unknown>>;

type SignalValues<TSignals extends SignalMap> = {
  [K in keyof TSignals]: TSignals[K] extends Signal<infer TValue> ? TValue : never;
};

export type LoadingStateTuple = [
  Signal<boolean>,
  () => void,
  () => void,
  <TResult>(operation: () => Promise<TResult>) => Promise<TResult>,
];

/**
 * Converts a provided signal into an observable within a given injection context.
 *
 * This function facilitates the transformation of a signal into an observable,
 * ensuring the conversion takes place within the specified dependency injection context.
 *
 * @template TSignal The type of value emitted by the signal and observable.
 * @param {Injector} injector - The dependency injection context used for the conversion.
 * @param {Signal<TSignal>} source - The signal to be converted into an observable.
 * @returns {Observable<TSignal>} An observable derived from the provided signal.
 */
export function signalToObservable<TSignal>(injector: Injector, source: Signal<TSignal>): Observable<TSignal> {
  return runInInjectionContext(injector, () => toObservable(source));
}

/**
 * Creates a persistent signal that synchronizes its state with a storage service.
 * The signal's initial value is determined from the storage or a provided default value.
 * Any updates to the signal are saved to the storage service, and null or undefined
 * values in the signal will remove the associated key from storage.
 *
 * @template TValue - The type of the signal's value.
 * @param {Injector} injector - The injector used to manage dependency injection context.
 * @param {string} storageKey - The key under which the value is stored in the storage service.
 * @param {TValue} defaultValue - The default value to use if no value exists in the storage.
 * @param {StorageService} storageService - The service used for persisting values.
 * @returns {WritableSignal<TValue>} A writable signal that is synchronized with the storage service.
 */
export function createPersistentSignal<TValue>(
  injector: Injector,
  storageKey: string,
  defaultValue: TValue,
  storageService: StorageService,
): WritableSignal<TValue> {
  const storedValue = storageService.get<TValue>(storageKey);
  const initialValue = storedValue ?? defaultValue;
  const state = signal<TValue>(initialValue);

  runInInjectionContext(injector, () => {
    toObservable(state)
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe((value) => {
        try {
          if (value === undefined || value === null) {
            storageService.remove(storageKey);
            return;
          }
          storageService.set(storageKey, value);
        } catch {
          // Fixes AML-DESK-7X: StorageService.set()/remove() can throw when the browser
          // blocks storage access (e.g. Firefox Enhanced Tracking Protection https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop).
          // The error is already reported internally by StorageService — swallow here
          // to prevent an unhandled exception from crashing the effect/subscription.
        }
      });
  });

  return state;
}

/**
 * Creates a computed signal that tracks the current and previous values of a given computation.
 *
 * @template TValue - The type of the computation's return value.
 * @param {() => TValue} computation - The computation function whose values will be tracked.
 * @returns {Signal<{ previous: TValue | undefined; current: TValue }>} A signal containing the previous and current values of the computation.
 */
export function createComputedWithPrevious<TValue>(
  computation: () => TValue,
): Signal<{ previous: TValue | undefined; current: TValue }> {
  let previous: TValue | undefined;
  return computed(() => {
    const current = computation();
    const snapshot = { previous, current };
    previous = current;
    return snapshot;
  });
}

/**
 * Creates a debounced signal that updates its value after a specified delay,
 * based on the provided source signal. The function ensures cleanup when
 * the associated injector's context is destroyed.
 *
 * @template TValue - The type of the source signal's value.
 * @param {Signal<TValue>} sourceSignal - The source signal to debounce.
 * @param {number} delay - The debounce delay time in milliseconds.
 * @param {Injector} injector - The dependency injector used to manage the signal's lifecycle.
 * @returns {Signal<TValue>} A readonly debounced signal that updates its value after the specified delay.
 */
export function createDebouncedSignal<TValue>(
  sourceSignal: Signal<TValue>,
  delay: number,
  injector: Injector,
): Signal<TValue> {
  const debounced = signal<TValue>(sourceSignal());
  runInInjectionContext(injector, () => {
    const destroyReference = inject(DestroyRef);
    signalToObservable(injector, sourceSignal)
      .pipe(debounceTime(delay), takeUntilDestroyed(destroyReference))
      .subscribe((value) => debounced.set(value));
  });
  return debounced.asReadonly();
}

/**
 * Creates and returns a tuple that provides functionality for managing a loading state.
 *
 * The returned tuple contains the following:
 * - A readonly signal indicating the current loading state.
 * - A function to set the loading state to `true`.
 * - A function to set the loading state to `false`.
 * - A utility function `withLoading` that wraps an asynchronous operation, automatically managing the loading state.
 *
 * @returns {LoadingStateTuple} A tuple including the loading signal,
 * start-loading function, stop-loading function, and the `withLoading` utility method.
 */
export function createLoadingState(): LoadingStateTuple {
  const loadingSignal = signal<boolean>(false);
  const startLoading = (): void => loadingSignal.set(true);
  const stopLoading = (): void => loadingSignal.set(false);

  const withLoading = async <TResult>(operation: () => Promise<TResult>): Promise<TResult> => {
    startLoading();
    try {
      return await operation();
    } finally {
      stopLoading();
    }
  };

  return [loadingSignal.asReadonly(), startLoading, stopLoading, withLoading];
}

/**
 * Converts a map of signals into an observable that emits the combined signal values.
 *
 * @template TSignals - The type of the map of signals.
 * @param {Injector} injector - The injector used to manage the dependency injection.
 * @param {TSignals} signals - A map of signals where each key maps to a specific signal.
 * @returns {Observable<SignalValues<TSignals>>} An observable that emits the combined values of the signals in the map.
 */
export function signalsToObservable<TSignals extends SignalMap>(
  injector: Injector,
  signals: TSignals,
): Observable<SignalValues<TSignals>> {
  const combined = computed<SignalValues<TSignals>>(() => {
    const entries = Object.entries(signals).map(([key, signalInstance]) => [key, signalInstance()]);
    return Object.fromEntries(entries) as SignalValues<TSignals>;
  });
  return signalToObservable(injector, combined);
}

/**
 * Creates an optional signal with undefined as the initial value,
 * eliminating the need for eslint-disable comments.
 *
 * @template TValue The type of value the signal will hold (excluding undefined).
 * @returns {WritableSignal<TValue | undefined>} A writable signal initialized to undefined.
 */
export function createOptionalSignal<TValue>(): WritableSignal<TValue | undefined> {
  return signal<TValue | undefined>(undefined);
}

/**
 * Creates a pair of signals: one writable for internal use and one readonly for public exposure.
 * This pattern is commonly used in stores to encapsulate state while providing read access.
 *
 * @template TValue The type of value the signals will hold.
 * @param {TValue} initialValue - The initial value for both signals.
 * @returns {[WritableSignal<TValue>, Signal<TValue>]} A tuple containing the writable and readonly signals.
 */
export function createSignalPair<TValue>(initialValue: TValue): [WritableSignal<TValue>, Signal<TValue>] {
  const writable = signal(initialValue);
  return [writable, writable.asReadonly()];
}
